import { deployEnv } from "../constants";

const SESSION_KEY = "mass_ticketing_session_id";

/**
 * Canonical event names. The Klaviyo metric uses the string as-is; the GTM
 * dataLayer receives a snake_case version of the same name.
 */
export const EVENTS = Object.freeze({
  // Anonymous — read these from GTM, not Klaviyo (see spec section 5.1).
  VIEWED_BOOKING_PAGE: "Viewed Booking Page",
  ADDED_TICKET: "Added Ticket",
  REMOVED_TICKET: "Removed Ticket",
  CLICKED_CHECKOUT: "Clicked Checkout",
  LOGIN_WALL_SHOWN: "Login Wall Shown",
  // Identified.
  LOGGED_IN: "Logged In",
  STARTED_CHECKOUT: "Started Checkout",
  VIEWED_BOOKING_STEP: "Viewed Booking Step",
  SUBMITTED_GUESTS_INFO: "Submitted Guests Info",
  ACCEPTED_TERMS: "Accepted Terms",
  INITIATED_PAYMENT: "Initiated Payment",
  // Failure / exit.
  ADD_TO_CART_FAILED: "Add To Cart Failed",
  CART_EXPIRED: "Cart Expired",
});

const toSnakeCase = (name) => name.toLowerCase().replace(/\s+/g, "_");

/**
 * Fabricated guest emails are minted as `${timestamp}-${index}-gguest-${realEmail}`
 * by GuestsInfo.jsx and by the backend mass-ticketing serializer. Identifying
 * them would create junk Klaviyo profiles and wreck list health.
 */
export const isGeneratedGuestEmail = (email) =>
  typeof email === "string" && email.includes("-gguest-");

const makeFallbackId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

/**
 * Prefers a real RFC 4122 UUID via crypto.randomUUID() (per the module's
 * documented `getSessionId() -> string — UUID` contract). Falls back to a
 * compact non-UUID id when the API is unavailable (older browsers,
 * non-secure contexts) or throws.
 */
const makeId = () => {
  try {
    if (
      typeof crypto !== "undefined" &&
      typeof crypto.randomUUID === "function"
    ) {
      return crypto.randomUUID();
    }
  } catch (e) {
    // fall through to the non-UUID fallback below
  }
  return makeFallbackId();
};

let cachedSessionId = null;

/**
 * A per-tab id used to stitch a visitor's anonymous pre-login activity to the
 * person they turn out to be once they log in. Falls back to an in-memory value
 * when sessionStorage is unavailable (private browsing).
 */
export const getSessionId = () => {
  if (cachedSessionId) return cachedSessionId;
  try {
    const stored = sessionStorage.getItem(SESSION_KEY);
    if (stored) {
      cachedSessionId = stored;
      return cachedSessionId;
    }
    cachedSessionId = makeId();
    sessionStorage.setItem(SESSION_KEY, cachedSessionId);
  } catch (e) {
    cachedSessionId = cachedSessionId || makeId();
  }
  return cachedSessionId;
};

let currentUserId = null;

const baseProps = () => {
  const props = { sessionId: getSessionId(), deployEnv };
  if (currentUserId != null) props.userId = currentUserId;
  return props;
};

/**
 * Hand a call to Klaviyo, or queue it if klaviyo.js has not loaded yet.
 *
 * The onsite script is async. A bare presence guard silently drops calls made
 * before it arrives, which left already-logged-in sessions permanently
 * unidentified -- and Klaviyo does not transmit events for unidentified
 * browsers, so the whole session was lost.
 *
 * window._learnq is the queue klaviyo.js actually reads. Verified against the
 * live loader for this account, which does:
 *   window._learnq = window._learnq || [];
 *   window._learnq.push(["account", companyId]);
 * Entries are [method, ...args], exactly what we push. The script drains the
 * array on load, so early calls replay instead of vanishing.
 *
 * Klaviyo drains BOTH _learnq and _klOnsite on load, so either would function.
 * (An earlier version of this comment claimed _klOnsite is never read — that was
 * wrong. The 9KB loader at static.klaviyo.com contains no reference to it, but
 * the drain lives in a sharedUtils chunk the loader pulls in afterwards.)
 *
 * We use _learnq for two concrete reasons:
 *   1. Its drain HOISTS account/cookieDomain/identify to the front of the replay.
 *      That matters here: identifyUser() resolves from api.auth.me(), which can
 *      land after several track() calls have already queued. Without hoisting,
 *      those events replay before identity is established and Klaviyo discards
 *      them as anonymous.
 *   2. _klOnsite's replay path coerces the last argument into a callback slot,
 *      duplicating the props object. Harmless, but noise we don't need.
 *
 * Never throws.
 */
const callKlaviyo = (args) => {
  try {
    const method = args[0];
    if (window.klaviyo && typeof window.klaviyo[method] === "function") {
      window.klaviyo[method].apply(window.klaviyo, args.slice(1));
      return;
    }
    window._learnq = window._learnq || [];
    window._learnq.push(args);
  } catch (e) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn("[tracking] klaviyo call failed", args[0], e);
    }
  }
};

/**
 * Fire-and-forget. Never throws, never returns a value, never blocks.
 * A failure here must never affect the booking flow.
 */
export const track = (eventName, props = {}) => {
  try {
    const payload = { ...baseProps(), ...props };
    callKlaviyo(["track", eventName, payload]);
    try {
      if (window.dataLayer && typeof window.dataLayer.push === "function") {
        window.dataLayer.push({ event: toSnakeCase(eventName), ...payload });
      }
    } catch (e) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("[tracking] dataLayer.push failed", eventName, e);
      }
    }
  } catch (e) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn("[tracking] failed", eventName, e);
    }
  }
  return undefined;
};

/**
 * Attaches subsequent events to a real person. Email only — see spec section 5.2
 * for why external_id is deliberately omitted.
 */
export const identifyUser = (user) => {
  try {
    const email = user && user.email;
    if (!email || isGeneratedGuestEmail(email)) return undefined;
    currentUserId = user.pk != null ? user.pk : null;
    const profile = { $email: email };
    if (user.firstName) profile.$first_name = user.firstName;
    if (user.lastName) profile.$last_name = user.lastName;
    callKlaviyo(["identify", profile]);
  } catch (e) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn("[tracking] identifyUser failed", e);
    }
  }
  return undefined;
};

/**
 * Clears our own identity state on logout.
 *
 * We deliberately do NOT call window.klaviyo.reset(). Klaviyo's onsite API has
 * no reset method — its surface is openForm, identify, track, trackViewedItem,
 * account, cookieDomain, isIdentified, cacheEvent, sendCachedEvents and
 * getGroupMembership. An earlier version called it behind a
 * `typeof window.klaviyo.reset === "function"` guard, which looks safe but never
 * is: once klaviyo.js binds, window.klaviyo is a Proxy whose get-trap returns a
 * function for EVERY key, so that check can never be false. The call therefore
 * fired on every logout in production and logged
 * `Unable to process event: reset,...` while clearing nothing.
 *
 * Consequence to be aware of: on a shared device, Klaviyo's cookie still points
 * at the previous profile after logout. It is corrected on the next login, when
 * identifyUser() calls identify() with the new email.
 *
 * General rule for this module: never use a typeof check against window.klaviyo
 * to test whether a method exists. It is not a capability test.
 */
export const resetIdentity = () => {
  try {
    currentUserId = null;
  } catch (e) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn("[tracking] resetIdentity failed to clear currentUserId", e);
    }
  }
  return undefined;
};
